import Button from '@atlaskit/button/default/button';

import Heading from '@atlaskit/heading/heading';

import ListChecklistIcon from '@atlaskit/icon/core/list-checklist';

import { RovoIcon } from '@atlaskit/logo';

import { Box } from '@atlaskit/primitives/box';

import { Inline } from '@atlaskit/primitives/inline';

import { Stack } from '@atlaskit/primitives/stack';

import { Text } from '@atlaskit/primitives/text';

import { xcss } from '@atlaskit/primitives/xcss/xcss';

import Skeleton from '@atlaskit/skeleton';

import { token } from '@atlaskit/tokens';

import { type JSX, useState, useCallback } from 'react';

import { FormattedMessage } from 'react-intl';

export type BannerUIProps = {
	isLoading: boolean;
	messageCount: number;
	renderMessages: () => React.ReactNode;
};

const bannerStyles = xcss({
	backgroundColor: 'elevation.surface.sunken',
	borderRadius: 'radius.xlarge',
	paddingBlockStart: 'space.150',
	paddingBlockEnd: 'space.150',
	paddingInline: 'space.300',
	width: '100%',
});

const headerInlineStyles = xcss({
	paddingInlineStart: 'space.100',
});

const skeletonInlineStyles = xcss({
	marginTop: 'space.100',
});

const skeletonCardStyles = xcss({
	backgroundColor: 'elevation.surface',
	borderRadius: 'radius.small',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	padding: 'space.150',
	flexGrow: '1',
});

const COLLAPSE_DURATION = 300;

// ms

export const BannerUI = ({
	isLoading,
	messageCount,
	renderMessages,
}: BannerUIProps): JSX.Element => {
	const [isCollapsed, setIsCollapsed] = useState(false);
	const handleToggle = useCallback(() => {
		setIsCollapsed((prev) => !prev);
	}, []);

	const showEmptyState = !isLoading && messageCount === 0;

	return (
		<Box xcss={bannerStyles}>
			<Stack space="space.0">
				<Inline alignBlock="center" spread="space-between">
					<Inline alignBlock="center" space="space.100" xcss={headerInlineStyles}>
						<Inline alignBlock="center" space="space.200">
							<ListChecklistIcon label="" color={token('color.icon.subtle')} />
							{isLoading && <RovoIcon appearance="brand" size="xsmall" label="" />}
						</Inline>
						{isLoading ? (
							<Text color="color.text">
								<FormattedMessage
									id="post-office.project-assets-ai-suggestions.header.loading"
									defaultMessage="Rovo is analyzing…"
									description="Loading state header text shown while AI suggestions are being fetched"
								/>
							</Text>
						) : (
							<Heading size="small" color="color.text">
								<FormattedMessage
									id="post-office.project-assets-ai-suggestions.header.loaded"
									defaultMessage="{count, plural, one {# suggested action} other {# suggested actions}}"
									description="Header text showing how many AI suggested actions are available"
									values={{ count: messageCount }}
								/>
							</Heading>
						)}
					</Inline>
					{!isLoading && !showEmptyState ? (
						<Button appearance="subtle" onClick={handleToggle}>
							{isCollapsed ? (
								<FormattedMessage
									id="post-office.project-assets-ai-suggestions.toggle.show"
									defaultMessage="Show"
									description="Button to expand the AI suggestions banner"
								/>
							) : (
								<FormattedMessage
									id="post-office.project-assets-ai-suggestions.toggle.hide"
									defaultMessage="Hide"
									description="Button to collapse the AI suggestions banner"
								/>
							)}
						</Button>
					) : (
						<span
							aria-hidden="true"
							style={{ display: 'inline-block', height: '32px', minWidth: '60px' }}
						/>
					)}
				</Inline>

				{/* Collapsible content — grid-template-rows animation avoids
				    hardcoded max-height that would silently clip tall content */}
				<div
					style={{
						display: 'grid',
						gridTemplateRows: isCollapsed ? '0fr' : '1fr',
						transition: `grid-template-rows ${COLLAPSE_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
					}}
				>
					<div style={{ overflow: 'hidden', paddingBottom: '8px' }}>
						{/* Loading skeleton */}
						{isLoading && (
							<Inline space="space.150" alignBlock="start" xcss={skeletonInlineStyles}>
								{[0, 1, 2].map((i) => (
									<Box key={i} xcss={skeletonCardStyles}>
										<Stack space="space.100">
											<Inline space="space.100" alignBlock="center">
												<Skeleton height="32px" width="32px" isShimmering />
												<Skeleton height="16px" isShimmering width="60%" />
											</Inline>
											<Skeleton height="12px" isShimmering width="90%" />
											<Skeleton height="12px" isShimmering width="90%" />
											<Inline alignInline="end">
												<Skeleton height="32px" width="80px" isShimmering />
											</Inline>
										</Stack>
									</Box>
								))}
							</Inline>
						)}

						{/* Empty state */}
						{showEmptyState && (
							<Text color="color.text.subtle">
								<FormattedMessage
									id="post-office.project-assets-ai-suggestions.empty-state"
									defaultMessage="No suggestions available right now."
									description="Empty state text shown when no AI suggestions are available"
								/>
							</Text>
						)}

						{/* Message list */}
						{!isLoading && !showEmptyState && renderMessages()}
					</div>
				</div>
			</Stack>
		</Box>
	);
};
